// PlansViewport3D.tsx — 3D viewport for Plans/Building mode
// Renders roof elements, wall sections, footing sections, door/window types as 3D geometry
// Uses R3F Canvas with orbit controls mirroring the SiteViewer3D camera pattern

import { useRef, useMemo, useEffect } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { Grid, Environment } from '@react-three/drei';
import * as THREE from 'three';
import GridLabels, { snapGridExtent } from './viewer/GridLabels';
import { useShallowStore } from '../store/useStore';
import { computeFootprint } from '../utils/buildingFootprint';
import type { PlansData, RoofElement, WallSection, FootingSection, DoorType, WindowType, Elevation, PanelData } from '../types/panelvar';

// ── 3D primitives for each roof element type ─────────────────────────────────

function DomeMesh({ el }: { el: RoofElement }) {
  const r = (el.diameter ?? 0) / 2;
  if (r <= 0) return null;
  return (
    <group position={[el.x, 0, el.y]}>
      <mesh>
        <sphereGeometry args={[r, 48, 32, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#7ba0c4" roughness={0.3} metalness={0.2} side={THREE.DoubleSide} />
      </mesh>
      {/* Ring at base */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[r - 4, r + 4, 64]} />
        <meshStandardMaterial color="#5a7a9a" roughness={0.6} />
      </mesh>
    </group>
  );
}

function BarrelVaultMesh({ el }: { el: RoofElement }) {
  const span = el.diameter ?? 0;
  const length = el.length ?? 0;
  if (span <= 0 || length <= 0) return null;
  const r = span / 2;
  const rot = ((el.rotation ?? 0) * Math.PI) / 180;

  // Barrel vault: half-cylinder
  const geo = useMemo(() => {
    const g = new THREE.CylinderGeometry(r, r, length, 32, 1, true, 0, Math.PI);
    return g;
  }, [r, length]);

  return (
    <group position={[el.x, 0, el.y]} rotation={[0, -rot, 0]}>
      <mesh geometry={geo} rotation={[0, Math.PI / 2, Math.PI / 2]}>
        <meshStandardMaterial color="#6abf8a" roughness={0.4} metalness={0.1} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function GableMesh({ el }: { el: RoofElement }) {
  const w = el.width ?? 0;
  const h = el.ridgeHeight ?? 0;
  if (w <= 0 || h <= 0) return null;
  const len = el.length ?? w * 0.5; // default depth = half width
  const rot = ((el.rotation ?? 0) * Math.PI) / 180;

  const geo = useMemo(() => {
    // Triangular prism: two triangular faces + two sloped sides
    const shape = new THREE.Shape();
    shape.moveTo(-w / 2, 0);
    shape.lineTo(0, h);
    shape.lineTo(w / 2, 0);
    shape.closePath();
    const extrudeSettings = { depth: len, bevelEnabled: false };
    return new THREE.ExtrudeGeometry(shape, extrudeSettings);
  }, [w, h, len]);

  return (
    <group position={[el.x, 0, el.y]} rotation={[0, -rot, 0]}>
      <mesh geometry={geo} position={[0, 0, -len / 2]}>
        <meshStandardMaterial color="#d9a04a" roughness={0.5} metalness={0.1} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function FlatRoofMesh({ el }: { el: RoofElement }) {
  const w = el.width ?? 0;
  const l = el.length ?? w;
  if (w <= 0) return null;
  const thickness = 12;      // 12" slab thickness
  const deckElev  = el.ridgeHeight ?? 304;  // roofline elevation from panel data
  return (
    <group position={[el.x, deckElev, el.y]}>
      {/* Roof slab */}
      <mesh position={[0, thickness / 2, 0]}>
        <boxGeometry args={[w, thickness, l]} />
        <meshStandardMaterial color="#b0b0b0" roughness={0.7} metalness={0.05} />
      </mesh>
    </group>
  );
}

function RoofElement3D({ el }: { el: RoofElement }) {
  switch (el.type) {
    case 'dome':         return <DomeMesh el={el} />;
    case 'barrel-vault': return <BarrelVaultMesh el={el} />;
    case 'gable':        return <GableMesh el={el} />;
    case 'flat':         return <FlatRoofMesh el={el} />;
    default:             return null;
  }
}

// ── 3D primitives for non-roof plan types ────────────────────────────────────

/** Wall section — extruded cross-section showing a concrete wall with footing */
function WallSection3D({ ws: _ws, index }: { ws: WallSection; index: number }) {
  const wallH = 120;   // 10' wall height
  const wallT = 10;    // 10" thick
  const wallW = 240;   // 20' depth (into screen)
  const footW = 36;    // 3' footing width
  const footH = 18;    // 18" footing depth
  const x = index * 360;

  return (
    <group position={[x, 0, 0]}>
      {/* Wall */}
      <mesh position={[0, wallH / 2 + footH, 0]}>
        <boxGeometry args={[wallT, wallH, wallW]} />
        <meshStandardMaterial color="#b0b0b0" roughness={0.7} />
      </mesh>
      {/* Footing */}
      <mesh position={[0, footH / 2, 0]}>
        <boxGeometry args={[footW, footH, wallW]} />
        <meshStandardMaterial color="#8a8a8a" roughness={0.8} />
      </mesh>
      {/* Label */}
      <mesh position={[0, wallH + footH + 20, 0]} rotation={[0, 0, 0]}>
        <boxGeometry args={[30, 30, 2]} />
        <meshStandardMaterial color="#3b82f6" />
      </mesh>
    </group>
  );
}

/** Footing section — pad footing with stem wall */
function FootingSection3D({ fs: _fs, index }: { fs: FootingSection; index: number }) {
  const padW = 96;     // 8' wide pad
  const padH = 36;     // 3' deep pad
  const padD = 96;     // 8' deep
  const stemW = 12;    // 12" stem wall
  const stemH = 48;    // 4' stem wall
  const x = index * 240;

  return (
    <group position={[x, 0, 0]}>
      {/* Pad footing */}
      <mesh position={[0, padH / 2, 0]}>
        <boxGeometry args={[padW, padH, padD]} />
        <meshStandardMaterial color="#999" roughness={0.8} />
      </mesh>
      {/* Stem wall */}
      <mesh position={[0, padH + stemH / 2, 0]}>
        <boxGeometry args={[stemW, stemH, padD]} />
        <meshStandardMaterial color="#aaa" roughness={0.7} />
      </mesh>
      {/* Rebar dots (small cylinders in concrete) */}
      {[-padW / 4, padW / 4].map((rx, i) => (
        <mesh key={i} position={[rx, padH / 2, padD / 2 + 1]} rotation={[-Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[2, 2, 2, 8]} />
          <meshStandardMaterial color="#333" />
        </mesh>
      ))}
    </group>
  );
}

/** Door type — 3D door with frame and panel */
function DoorType3D({ dt, index }: { dt: DoorType; index: number }) {
  const w = dt.width || 72;
  const h = dt.height || 84;
  const depth = 4;       // door thickness
  const frameW = 4;      // frame width
  const x = index * (w + 80);

  return (
    <group position={[x, 0, 0]}>
      {/* Door frame */}
      {/* Left jamb */}
      <mesh position={[-w / 2 - frameW / 2, h / 2, 0]}>
        <boxGeometry args={[frameW, h, depth + 4]} />
        <meshStandardMaterial color="#6b4c2a" roughness={0.7} />
      </mesh>
      {/* Right jamb */}
      <mesh position={[w / 2 + frameW / 2, h / 2, 0]}>
        <boxGeometry args={[frameW, h, depth + 4]} />
        <meshStandardMaterial color="#6b4c2a" roughness={0.7} />
      </mesh>
      {/* Head */}
      <mesh position={[0, h + frameW / 2, 0]}>
        <boxGeometry args={[w + frameW * 2, frameW, depth + 4]} />
        <meshStandardMaterial color="#6b4c2a" roughness={0.7} />
      </mesh>
      {/* Door panel */}
      <mesh position={[0, h / 2, 0]}>
        <boxGeometry args={[w, h, depth]} />
        <meshStandardMaterial color="#8B6914" roughness={0.5} metalness={0.1} />
      </mesh>
      {/* Handle */}
      <mesh position={[w / 2 - 8, h / 2, depth / 2 + 2]}>
        <sphereGeometry args={[3, 16, 16]} />
        <meshStandardMaterial color="#c0a040" metalness={0.8} roughness={0.2} />
      </mesh>
    </group>
  );
}

/** Window type — 3D window with frame, mullions, and glass */
function WindowType3D({ wt, index }: { wt: WindowType; index: number }) {
  const w = wt.width || 48;
  const h = wt.height || 60;
  const depth = 3;
  const frameW = 3;
  const x = index * (w + 80);
  const sillH = 36;  // 3' sill height

  return (
    <group position={[x, sillH, 0]}>
      {/* Window frame */}
      {/* Left */}
      <mesh position={[-w / 2 - frameW / 2, h / 2, 0]}>
        <boxGeometry args={[frameW, h, depth + 2]} />
        <meshStandardMaterial color="#f0f0f0" roughness={0.5} />
      </mesh>
      {/* Right */}
      <mesh position={[w / 2 + frameW / 2, h / 2, 0]}>
        <boxGeometry args={[frameW, h, depth + 2]} />
        <meshStandardMaterial color="#f0f0f0" roughness={0.5} />
      </mesh>
      {/* Top */}
      <mesh position={[0, h + frameW / 2, 0]}>
        <boxGeometry args={[w + frameW * 2, frameW, depth + 2]} />
        <meshStandardMaterial color="#f0f0f0" roughness={0.5} />
      </mesh>
      {/* Sill */}
      <mesh position={[0, -frameW / 2, 0]}>
        <boxGeometry args={[w + frameW * 2 + 4, frameW, depth + 6]} />
        <meshStandardMaterial color="#ddd" roughness={0.6} />
      </mesh>
      {/* Glass */}
      <mesh position={[0, h / 2, 0]}>
        <boxGeometry args={[w, h, 1]} />
        <meshStandardMaterial color="#a0d4f0" transparent opacity={0.4} roughness={0.1} metalness={0.2} />
      </mesh>
      {/* Cross mullion vertical */}
      <mesh position={[0, h / 2, depth / 2]}>
        <boxGeometry args={[2, h, 2]} />
        <meshStandardMaterial color="#f0f0f0" roughness={0.5} />
      </mesh>
      {/* Cross mullion horizontal */}
      <mesh position={[0, h / 2, depth / 2]}>
        <boxGeometry args={[w, 2, 2]} />
        <meshStandardMaterial color="#f0f0f0" roughness={0.5} />
      </mesh>
    </group>
  );
}

/** Floor plan — 3D slab with raised perimeter */
function FloorPlan3D({ index, slabW, slabD, cx, cz }: { index: number; slabW: number; slabD: number; cx: number; cz: number }) {
  const slabH = 8;     // 8" thick slab
  const wallH = 24;    // 2' perimeter curb
  const wallT = 8;     // 8" curb width
  const y = index * 120;

  return (
    <group position={[cx, y, cz]}>
      {/* Slab */}
      <mesh position={[0, slabH / 2, 0]}>
        <boxGeometry args={[slabW, slabH, slabD]} />
        <meshStandardMaterial color="#c0c0c0" roughness={0.8} />
      </mesh>
      {/* Perimeter curb — front */}
      <mesh position={[0, slabH + wallH / 2, slabD / 2 - wallT / 2]}>
        <boxGeometry args={[slabW, wallH, wallT]} />
        <meshStandardMaterial color="#a8a8a8" roughness={0.7} />
      </mesh>
      {/* Perimeter curb — back */}
      <mesh position={[0, slabH + wallH / 2, -slabD / 2 + wallT / 2]}>
        <boxGeometry args={[slabW, wallH, wallT]} />
        <meshStandardMaterial color="#a8a8a8" roughness={0.7} />
      </mesh>
      {/* Perimeter curb — left */}
      <mesh position={[-slabW / 2 + wallT / 2, slabH + wallH / 2, 0]}>
        <boxGeometry args={[wallT, wallH, slabD]} />
        <meshStandardMaterial color="#a8a8a8" roughness={0.7} />
      </mesh>
      {/* Perimeter curb — right */}
      <mesh position={[slabW / 2 - wallT / 2, slabH + wallH / 2, 0]}>
        <boxGeometry args={[wallT, wallH, slabD]} />
        <meshStandardMaterial color="#a8a8a8" roughness={0.7} />
      </mesh>
    </group>
  );
}

// ── Simple orbit camera (top-down by default for plan view) ──────────────────

function OrbitCamera({ center, size }: { center: [number, number, number]; size: number }) {
  const { camera, gl, invalidate } = useThree();
  const state = useRef({ theta: 0, phi: 0.4, dist: size * 2 });
  const dragging = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    state.current.dist = size * 2;
    invalidate();
  }, [size, invalidate]);

  useFrame(() => {
    const s = state.current;
    const x = center[0] + s.dist * Math.sin(s.phi) * Math.sin(s.theta);
    const y = center[1] + s.dist * Math.cos(s.phi);
    const z = center[2] + s.dist * Math.sin(s.phi) * Math.cos(s.theta);
    camera.position.set(x, y, z);
    camera.lookAt(center[0], center[1], center[2]);
  });

  useEffect(() => {
    const el = gl.domElement;
    const inv = invalidate;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button === 2 || e.button === 1) {
        dragging.current = true;
        last.current = { x: e.clientX, y: e.clientY };
        el.setPointerCapture(e.pointerId);
      }
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging.current) return;
      const dx = e.clientX - last.current.x;
      const dy = e.clientY - last.current.y;
      last.current = { x: e.clientX, y: e.clientY };
      state.current.theta -= dx * 0.005;
      state.current.phi = Math.max(0.05, Math.min(Math.PI - 0.05, state.current.phi + dy * 0.005));
      inv();
    };
    const onPointerUp = (e: PointerEvent) => {
      dragging.current = false;
      try { el.releasePointerCapture(e.pointerId); } catch { /* */ }
    };
    const onWheel = (e: WheelEvent) => {
      state.current.dist = Math.max(100, Math.min(100000, state.current.dist * (e.deltaY > 0 ? 1.1 : 0.9)));
      inv();
    };
    const onCtx = (e: Event) => e.preventDefault();

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('contextmenu', onCtx);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('contextmenu', onCtx);
    };
  }, [gl, invalidate]);

  return null;
}

// ── Main component ───────────────────────────────────────────────────────────

interface Props {
  plans: PlansData | null;
  activeTab: string;
  elevations?: Elevation[];
  panels?: PanelData[];
}

/** Determine what to render and compute bounding info */
function useSceneContent(plans: PlansData | null, activeTab: string) {
  return useMemo(() => {
    if (!plans) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };

    // Specific roof plan
    if (activeTab.startsWith('roof-')) {
      const roofId = activeTab.replace('roof-', '');
      const rp = plans.roofPlans.find(r => r.id === roofId) ?? plans.roofPlans[0];
      if (!rp || rp.elements.length === 0) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, maxY = 0;
      for (const el of rp.elements) {
        const r = Math.max(el.diameter ?? 0, el.width ?? 0, el.length ?? 0) / 2;
        minX = Math.min(minX, el.x - r); maxX = Math.max(maxX, el.x + r);
        minZ = Math.min(minZ, el.y - r); maxZ = Math.max(maxZ, el.y + r);
        if (el.type === 'dome') maxY = Math.max(maxY, (el.diameter ?? 0) / 2);
        if (el.type === 'barrel-vault') maxY = Math.max(maxY, (el.diameter ?? 0) / 2);
        if (el.type === 'gable') maxY = Math.max(maxY, el.ridgeHeight ?? 0);
        if (el.type === 'flat') maxY = Math.max(maxY, (el.ridgeHeight ?? 304) + 12);
      }
      const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
      return { type: 'roof' as const, roofPlan: rp, center: [cx, maxY / 2, cz] as [number, number, number], size: Math.max(maxX - minX, maxZ - minZ, 600), bounds: { minX, maxX, minZ, maxZ } };
    }

    // "roofs" parent = show first roof plan
    if (activeTab === 'roofs' && plans.roofPlans.length > 0) {
      const rp = plans.roofPlans[0];
      if (rp.elements.length === 0) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, maxY = 0;
      for (const el of rp.elements) {
        const r = Math.max(el.diameter ?? 0, el.width ?? 0, el.length ?? 0) / 2;
        minX = Math.min(minX, el.x - r); maxX = Math.max(maxX, el.x + r);
        minZ = Math.min(minZ, el.y - r); maxZ = Math.max(maxZ, el.y + r);
        if (el.type === 'dome') maxY = Math.max(maxY, (el.diameter ?? 0) / 2);
        if (el.type === 'barrel-vault') maxY = Math.max(maxY, (el.diameter ?? 0) / 2);
        if (el.type === 'gable') maxY = Math.max(maxY, el.ridgeHeight ?? 0);
        if (el.type === 'flat') maxY = Math.max(maxY, (el.ridgeHeight ?? 304) + 12);
      }
      const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
      return { type: 'roof' as const, roofPlan: rp, center: [cx, maxY / 2, cz] as [number, number, number], size: Math.max(maxX - minX, maxZ - minZ, 600), bounds: { minX, maxX, minZ, maxZ } };
    }

    // Wall section
    if (activeTab.startsWith('wall-')) {
      const wsId = activeTab.replace('wall-', '');
      const ws = plans.wallSections.find(w => w.id === wsId);
      if (!ws) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      return { type: 'wall' as const, wallSection: ws, index: plans.wallSections.indexOf(ws), center: [0, 80, 0] as [number, number, number], size: 400 };
    }
    if (activeTab === 'walls') {
      const n = plans.wallSections.length;
      if (!n) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      return { type: 'walls-all' as const, center: [(n - 1) * 180, 80, 0] as [number, number, number], size: Math.max(n * 360, 600) };
    }

    // Footing section
    if (activeTab.startsWith('footing-')) {
      const fsId = activeTab.replace('footing-', '');
      const fs = plans.footingSections.find(f => f.id === fsId);
      if (!fs) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      return { type: 'footing' as const, footingSection: fs, index: plans.footingSections.indexOf(fs), center: [0, 40, 0] as [number, number, number], size: 300 };
    }
    if (activeTab === 'footings') {
      const n = plans.footingSections.length;
      if (!n) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      return { type: 'footings-all' as const, center: [(n - 1) * 120, 40, 0] as [number, number, number], size: Math.max(n * 240, 600) };
    }

    // Door type
    if (activeTab.startsWith('door-')) {
      const dtId = activeTab.replace('door-', '');
      const dt = plans.doorTypes.find(d => d.id === dtId);
      if (!dt) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      return { type: 'door' as const, doorType: dt, index: plans.doorTypes.indexOf(dt), center: [0, dt.height / 2, 0] as [number, number, number], size: Math.max(dt.width, dt.height, 200) };
    }
    if (activeTab === 'doors') {
      const n = plans.doorTypes.length;
      if (!n) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      return { type: 'doors-all' as const, center: [(n - 1) * 100, 80, 0] as [number, number, number], size: Math.max(n * 200, 600) };
    }

    // Window type
    if (activeTab.startsWith('window-')) {
      const wtId = activeTab.replace('window-', '');
      const wt = plans.windowTypes.find(w => w.id === wtId);
      if (!wt) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      return { type: 'window' as const, windowType: wt, index: plans.windowTypes.indexOf(wt), center: [0, 36 + wt.height / 2, 0] as [number, number, number], size: Math.max(wt.width, wt.height, 200) };
    }
    if (activeTab === 'windows') {
      const n = plans.windowTypes.length;
      if (!n) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      return { type: 'windows-all' as const, center: [(n - 1) * 80, 60, 0] as [number, number, number], size: Math.max(n * 160, 600) };
    }

    // Floor plan
    if (activeTab.startsWith('floor-')) {
      const fpId = activeTab.replace('floor-', '');
      const fp = plans.floorPlans.find(f => f.id === fpId);
      if (!fp) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      return { type: 'floor' as const, floorPlan: fp, index: plans.floorPlans.indexOf(fp), center: [0, 20, 0] as [number, number, number], size: 1200 };
    }
    if (activeTab === 'floors') {
      const n = plans.floorPlans.length;
      if (!n) return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
      return { type: 'floors-all' as const, center: [0, (n - 1) * 60, 0] as [number, number, number], size: 1200 };
    }

    // Overall / foundation — show composite building: floors + walls + roof
    if (activeTab === 'overall' || activeTab === 'foundation') {
      const rp = plans.roofPlans[0];
      // Determine bounding box from roof elements if available
      let minX = -480, maxX = 480, minZ = -240, maxZ = 240, maxY = 200;
      if (rp && rp.elements.length > 0) {
        minX = Infinity; maxX = -Infinity; minZ = Infinity; maxZ = -Infinity; maxY = 0;
        for (const el of rp.elements) {
          const r = Math.max(el.diameter ?? 0, el.width ?? 0, el.length ?? 0) / 2;
          minX = Math.min(minX, el.x - r); maxX = Math.max(maxX, el.x + r);
          minZ = Math.min(minZ, el.y - r); maxZ = Math.max(maxZ, el.y + r);
          if (el.type === 'dome') maxY = Math.max(maxY, (el.diameter ?? 0) / 2);
          if (el.type === 'barrel-vault') maxY = Math.max(maxY, (el.diameter ?? 0) / 2);
          if (el.type === 'gable') maxY = Math.max(maxY, el.ridgeHeight ?? 0);
          if (el.type === 'flat') maxY = Math.max(maxY, (el.ridgeHeight ?? 304) + 12);
        }
      }
      const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
      const sz = Math.max(maxX - minX, maxZ - minZ, 600);
      return {
        type: 'overall' as const,
        roofPlan: rp ?? null,
        floorCount: plans.floorPlans.length,
        wallCount: plans.wallSections.length,
        buildingBounds: { minX, maxX, minZ, maxZ },
        center: [cx, maxY / 3, cz] as [number, number, number],
        size: sz,
      };
    }

    return { type: 'empty' as const, center: [0, 0, 0] as [number, number, number], size: 600 };
  }, [plans, activeTab]);
}

export default function PlansViewport3D({ plans, activeTab, elevations, panels }: Props) {
  const ui = useShallowStore(s => ({ showGrid: s.ui.showGrid, showGround: s.ui.showGround, shadingMode: s.ui.shadingMode }));
  const scene = useSceneContent(plans, activeTab);

  // Compute real building footprint bounds from elevation chain
  const bldgBounds = useMemo(() => {
    if (!elevations || !panels || elevations.length === 0) return null;
    const fp = computeFootprint(elevations, panels);
    if (fp.walls.length === 0) return null;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const w of fp.walls) {
      minX = Math.min(minX, w.startX, w.endX);
      maxX = Math.max(maxX, w.startX, w.endX);
      // Footprint Y maps to 3D Z
      minZ = Math.min(minZ, w.startY, w.endY);
      maxZ = Math.max(maxZ, w.startY, w.endY);
    }
    return { minX, maxX, minZ, maxZ, w: maxX - minX, d: maxZ - minZ, cx: (minX + maxX) / 2, cz: (minZ + maxZ) / 2 };
  }, [elevations, panels]);

  const gridExtent = useMemo(() => {
    // For floor-related scenes, prefer building footprint bounds
    if (bldgBounds && (scene.type === 'floor' || scene.type === 'floors-all')) {
      return snapGridExtent(bldgBounds.minX, bldgBounds.maxX, bldgBounds.minZ, bldgBounds.maxZ);
    }
    const s = scene as Record<string, unknown>;
    const b = (s.bounds ?? s.buildingBounds) as { minX: number; maxX: number; minZ: number; maxZ: number } | undefined;
    if (b && 'minX' in b) return snapGridExtent(b.minX, b.maxX, b.minZ, b.maxZ);
    const half = scene.size / 2;
    return snapGridExtent(scene.center[0] - half, scene.center[0] + half, scene.center[2] - half, scene.center[2] + half);
  }, [scene, bldgBounds]);

  // Override center/size for floor scenes when real building bounds are available
  const effectiveCenter = useMemo<[number, number, number]>(() => {
    if (bldgBounds && (scene.type === 'floor' || scene.type === 'floors-all')) {
      return [bldgBounds.cx, 20, bldgBounds.cz];
    }
    return scene.center;
  }, [scene, bldgBounds]);
  const effectiveSize = useMemo(() => {
    if (bldgBounds && (scene.type === 'floor' || scene.type === 'floors-all')) {
      return Math.max(bldgBounds.w, bldgBounds.d, 600);
    }
    return scene.size;
  }, [scene, bldgBounds]);

  if (scene.type === 'empty') {
    return (
      <div className="building-canvas-empty">
        <p>No elements to render in 3D.</p>
        <p className="building-canvas-hint">Use the properties panel to add items, or select a specific item from the sidebar.</p>
      </div>
    );
  }

  return (
    <div className="viewer-root" style={{ width: '100%', height: '100%' }}>
      <Canvas
        frameloop="demand"
        camera={{ fov: 45, near: 0.1, far: 500000, position: [0, 2000, 2000] }}
        gl={{ antialias: true }}
        style={{ background: '#0d1117' }}
      >
        <OrbitCamera center={effectiveCenter} size={effectiveSize} />

        <ambientLight intensity={0.6} />
        <directionalLight position={[3000, 5000, 2000]} intensity={1.2} />
        <directionalLight position={[-2000, 1000, -1000]} intensity={0.4} />

        {ui.shadingMode === 'shaded' && (
          <Environment preset="warehouse" background={false} />
        )}

        {/* Roof elements */}
        {scene.type === 'roof' && scene.roofPlan.elements.map(el => (
          <RoofElement3D key={el.id} el={el} />
        ))}

        {/* Single wall section */}
        {scene.type === 'wall' && (
          <WallSection3D ws={scene.wallSection} index={0} />
        )}
        {/* All wall sections */}
        {scene.type === 'walls-all' && plans!.wallSections.map((ws, i) => (
          <WallSection3D key={ws.id} ws={ws} index={i} />
        ))}

        {/* Single footing */}
        {scene.type === 'footing' && (
          <FootingSection3D fs={scene.footingSection} index={0} />
        )}
        {/* All footings */}
        {scene.type === 'footings-all' && plans!.footingSections.map((fs, i) => (
          <FootingSection3D key={fs.id} fs={fs} index={i} />
        ))}

        {/* Single door */}
        {scene.type === 'door' && (
          <DoorType3D dt={scene.doorType} index={0} />
        )}
        {/* All doors */}
        {scene.type === 'doors-all' && plans!.doorTypes.map((dt, i) => (
          <DoorType3D key={dt.id} dt={dt} index={i} />
        ))}

        {/* Single window */}
        {scene.type === 'window' && (
          <WindowType3D wt={scene.windowType} index={0} />
        )}
        {/* All windows */}
        {scene.type === 'windows-all' && plans!.windowTypes.map((wt, i) => (
          <WindowType3D key={wt.id} wt={wt} index={i} />
        ))}

        {/* Single floor plan */}
        {scene.type === 'floor' && (
          <FloorPlan3D index={0} slabW={bldgBounds?.w ?? 960} slabD={bldgBounds?.d ?? 480} cx={bldgBounds?.cx ?? 0} cz={bldgBounds?.cz ?? 0} />
        )}
        {/* All floors stacked */}
        {scene.type === 'floors-all' && plans!.floorPlans.map((_fp, i) => (
          <FloorPlan3D key={_fp.id} index={i} slabW={bldgBounds?.w ?? 960} slabD={bldgBounds?.d ?? 480} cx={bldgBounds?.cx ?? 0} cz={bldgBounds?.cz ?? 0} />
        ))}

        {/* Overall composite: floor slab + walls + roof */}
        {scene.type === 'overall' && (() => {
          const b = scene.buildingBounds;
          const bw = b.maxX - b.minX || 960;
          const bd = b.maxZ - b.minZ || 480;
          const cx = (b.minX + b.maxX) / 2;
          const cz = (b.minZ + b.maxZ) / 2;
          const wallH = 144; // 12 ft walls
          const slabH = 6;
          return (
            <group>
              {/* Foundation slab */}
              <mesh position={[cx, -slabH / 2, cz]}>
                <boxGeometry args={[bw + 24, slabH, bd + 24]} />
                <meshStandardMaterial color="#909090" roughness={0.9} />
              </mesh>
              {/* Walls — four perimeter walls */}
              {/* Front wall (–Z) */}
              <mesh position={[cx, wallH / 2, cz - bd / 2]}>
                <boxGeometry args={[bw, wallH, 8]} />
                <meshStandardMaterial color="#c8b896" roughness={0.7} />
              </mesh>
              {/* Back wall (+Z) */}
              <mesh position={[cx, wallH / 2, cz + bd / 2]}>
                <boxGeometry args={[bw, wallH, 8]} />
                <meshStandardMaterial color="#c8b896" roughness={0.7} />
              </mesh>
              {/* Left wall (–X) */}
              <mesh position={[cx - bw / 2, wallH / 2, cz]}>
                <boxGeometry args={[8, wallH, bd]} />
                <meshStandardMaterial color="#b8a886" roughness={0.7} />
              </mesh>
              {/* Right wall (+X) */}
              <mesh position={[cx + bw / 2, wallH / 2, cz]}>
                <boxGeometry args={[8, wallH, bd]} />
                <meshStandardMaterial color="#b8a886" roughness={0.7} />
              </mesh>
              {/* Roof elements raised to wall top */}
              {scene.roofPlan && scene.roofPlan.elements.map(el => (
                <group key={el.id} position={[0, wallH, 0]}>
                  <RoofElement3D el={el} />
                </group>
              ))}
            </group>
          );
        })()}

        <Grid
          args={[gridExtent.width, gridExtent.depth]}
          cellSize={120}
          sectionSize={600}
          cellColor="#3a5a80"
          sectionColor="#5080b0"
          cellThickness={1.5}
          sectionThickness={2.5}
          fadeDistance={8000}
          visible={ui.showGrid}
          position={[gridExtent.cx, -1, gridExtent.cz]}
        />

        {ui.showGrid && <GridLabels extent={gridExtent} />}

        {ui.showGround && (
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[gridExtent.cx, -2, gridExtent.cz]}>
            <planeGeometry args={[gridExtent.width, gridExtent.depth]} />
            <meshStandardMaterial color="#1a1a2e" roughness={1} />
          </mesh>
        )}
      </Canvas>
    </div>
  );
}
